import { useState } from "react";
import { Redirect } from "wouter";
import { Users, MessageSquare, Star, Handshake, Flag, BadgeCheck, BookOpen } from "lucide-react";
import { V2_FEATURES } from "@shared/constants/features";
import { useCurrentUser } from "../lib/auth/useAuth";
import {
  useAdminUsers,
  useAdminFeedback,
  useSetUserDisabled,
  type AdminUser,
} from "../lib/api/useAdmin";
import { SponsorsPanel } from "../components/admin/SponsorsPanel";
import { ModerationPanel } from "../features/community/admin/ModerationPanel";
import { BadgesPanel } from "../features/community/admin/BadgesPanel";
import { AdminStoriesPanel } from "../features/stories/admin/AdminStoriesPanel";
import { AppShell } from "../components/AppShell";
import { PlayfulHero } from "../components/PlayfulHero";
import { TabNav } from "../components/ui/TabNav";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { InlineLoader, PageLoader } from "../components/ui/Spinner";
import { EmptyState } from "../components/ui/EmptyState";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { INPUT_CLASS } from "../lib/forms/inputClass";
import { cn } from "../lib/cn";
import { useToast } from "../lib/toast/ToastProvider";

const TABS = [
  { id: "users", label: "Accounts", icon: Users },
  { id: "feedback", label: "Feedback", icon: MessageSquare },
  { id: "sponsors", label: "Sponsors", icon: Handshake },
  { id: "moderation", label: "Moderation", icon: Flag, on: V2_FEATURES.community },
  { id: "badges", label: "Badges", icon: BadgeCheck, on: V2_FEATURES.community },
  { id: "stories", label: "Stories", icon: BookOpen, on: V2_FEATURES.stories },
] as const;

function fmt(d: string | null) {
  return d ? new Date(d).toLocaleDateString() : "—";
}

/**
 * Platform operator console at /admin.
 *
 * Shows ACCOUNTS, never child records — an operator has no business reading a
 * stranger's child's medical history, and the API doesn't return it either.
 */
export function AdminConsole() {
  const { data: user, isLoading } = useCurrentUser();
  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("users");
  const [search, setSearch] = useState("");

  if (isLoading) return <PageLoader label="Loading console…" />;

  // Same posture as the API: no such page for anyone without the flag.
  // <Redirect> navigates in an effect; calling setLocation here, during
  // render, made React warn "Cannot update a component while rendering a
  // different component" on every non-admin visit.
  if (!user?.isPlatformAdmin) return <Redirect to="/dashboard" />;

  return (
    <AppShell maxWidth="5xl" tone="emerald">
      <PlayfulHero
        accent="emerald"
        decor="compact"
        art="lock"
        eyebrow="Platform"
        title="Platform console"
        description="Accounts, feedback, sponsors, community moderation, badges and story review. Child records are deliberately not shown here."
      />

      {/* A wrapping pill tray (TabNav), so the row never runs off a phone. */}
      <TabNav
        label="Console sections"
        tabs={TABS.filter((t) => !("on" in t) || t.on).map(({ id, label, icon: Icon }) => ({
          id,
          label,
          icon: <Icon className="h-4 w-4" aria-hidden />,
        }))}
        activeTab={tab}
        onTabChange={setTab}
      />

      <div role="tabpanel" id={`tabpanel-${tab}`} aria-labelledby={`tab-${tab}`} className="mt-8">
        {tab === "users" && (
          <UsersPanel search={search} setSearch={setSearch} currentUserId={user.id} />
        )}
        {tab === "feedback" && <FeedbackPanel />}
        {tab === "sponsors" && <SponsorsPanel />}
        {tab === "moderation" && <ModerationPanel />}
        {tab === "badges" && <BadgesPanel />}
        {tab === "stories" && <AdminStoriesPanel />}
      </div>
    </AppShell>
  );
}

/** Status pills for an account (shared by the phone list and the table). */
function StatusPills({ user: u }: { user: AdminUser }) {
  return (
    <div className="flex flex-wrap gap-1">
      {u.isPlatformAdmin && (
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
          admin
        </span>
      )}
      {u.disabledAt ? (
        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
          suspended
        </span>
      ) : (
        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
          active
        </span>
      )}
      {u.mfaEnabled && (
        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800">
          2FA
        </span>
      )}
      {!u.termsAcceptedAt && (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          terms pending
        </span>
      )}
    </div>
  );
}

function UsersPanel({
  search,
  setSearch,
  currentUserId,
}: {
  search: string;
  setSearch: (s: string) => void;
  currentUserId: string;
}) {
  const { data, isLoading, error } = useAdminUsers(search);
  const setDisabled = useSetUserDisabled();
  const { publish } = useToast();
  const [pending, setPending] = useState<AdminUser | null>(null);

  if (isLoading) return <InlineLoader label="Loading accounts…" />;
  if (error) return <Card className="p-6 text-sm text-red-700">Couldn&apos;t load accounts.</Card>;
  if (!data) return null;

  // Platform admins and your own row have no action: the API refuses both, so
  // offering a button would only mislead.
  const action = (u: AdminUser) =>
    !u.isPlatformAdmin &&
    u.id !== currentUserId && (
      <Button
        variant={u.disabledAt ? "secondary" : "destructiveOutline"}
        size="sm"
        onClick={() => setPending(u)}
      >
        {u.disabledAt ? "Reinstate" : "Suspend"}
      </Button>
    );

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-neutral-600">
          <span className="font-semibold text-neutral-900">{data.total}</span> accounts ·{" "}
          <span className="font-semibold text-neutral-900">{data.active}</span> active
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search email or name…"
          className={cn(INPUT_CLASS, "mt-0 sm:max-w-xs")}
          aria-label="Search accounts"
        />
      </div>

      {data.users.length === 0 ? (
        <EmptyState art="lock" accent="emerald" title="No accounts" description="Nobody has signed up yet." />
      ) : (
        <>
          {/* Phone: one glass card per account (a table would scroll sideways). */}
          <ul className="mt-4 space-y-3 sm:hidden">
            {data.users.map((u) => (
              <li key={u.id} className="rounded-2xl bg-white/85 p-4 shadow-soft ring-1 ring-white">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-neutral-900">{u.fullName ?? "—"}</p>
                    <p className="break-all text-xs text-neutral-600">{u.email}</p>
                  </div>
                  {action(u)}
                </div>
                <p className="mt-2 text-xs text-neutral-600">
                  {u.childCount} {u.childCount === 1 ? "child" : "children"} · joined {fmt(u.createdAt)}
                </p>
                <div className="mt-2">
                  <StatusPills user={u} />
                </div>
              </li>
            ))}
          </ul>

          <table className="mt-4 hidden w-full border-separate border-spacing-y-2 text-sm sm:table">
            <thead className="text-left text-xs uppercase tracking-wide text-neutral-600">
              <tr>
                <th className="px-3 py-1">Account</th>
                <th className="px-3 py-1">Children</th>
                <th className="px-3 py-1">Joined</th>
                <th className="px-3 py-1">Status</th>
                <th className="px-3 py-1">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id} className="bg-white/85 shadow-soft">
                  <td className="rounded-l-2xl px-3 py-3">
                    <div className="font-medium text-neutral-900">{u.fullName ?? "—"}</div>
                    <div className="text-xs text-neutral-600">{u.email}</div>
                  </td>
                  <td className="px-3 py-3 text-neutral-700">{u.childCount}</td>
                  <td className="px-3 py-3 text-neutral-700">{fmt(u.createdAt)}</td>
                  <td className="px-3 py-3">
                    <StatusPills user={u} />
                  </td>
                  <td className="rounded-r-2xl px-3 py-3 text-right">{action(u)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title={pending?.disabledAt ? "Reinstate this account?" : "Suspend this account?"}
        description={
          pending?.disabledAt
            ? `${pending?.email} will be able to sign in again.`
            : `${pending?.email} will no longer be able to sign in. Their data is kept, and any share links they created keep working — revoke those separately if that matters.`
        }
        confirmLabel={pending?.disabledAt ? "Reinstate" : "Suspend"}
        isPending={setDisabled.isPending}
        destructive={!pending?.disabledAt}
        onConfirm={() => {
          if (!pending) return;
          const disabling = !pending.disabledAt;
          setDisabled.mutate(
            { id: pending.id, disabled: disabling },
            {
              onSuccess: () => {
                publish({
                  variant: "success",
                  title: disabling ? "Account suspended" : "Account reinstated",
                });
                setPending(null);
              },
              onError: () =>
                publish({ variant: "error", title: "Couldn't update that account" }),
            },
          );
        }}
      />
    </>
  );
}

function FeedbackPanel() {
  const { data, isLoading, error } = useAdminFeedback();
  if (isLoading) return <InlineLoader label="Loading feedback…" />;
  if (error) return <Card className="p-6 text-sm text-red-700">Couldn&apos;t load feedback.</Card>;
  if (!data || data.length === 0) {
    return (
      <EmptyState art="heart" accent="emerald" title="No feedback yet" description="Submissions will appear here." />
    );
  }
  return (
    <ul className="space-y-3">
      {data.map((f) => (
        <li key={f.id}>
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-600">
              <span className="font-medium text-neutral-900">{f.name ?? "Anonymous"}</span>
              {f.email && <span>· {f.email}</span>}
              <span>· {new Date(f.createdAt).toLocaleString()}</span>
              {f.rating !== null && (
                <span className="ml-auto inline-flex items-center gap-0.5 text-amber-500">
                  {Array.from({ length: f.rating }).map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden />
                  ))}
                  <span className="sr-only">{f.rating} out of 5</span>
                </span>
              )}
            </div>
            <p className="mt-2 whitespace-pre-line text-sm text-neutral-800">{f.feedback}</p>
            {/* Plain text, never a link: the path is stored masked (tokens and
                ids), and it came from a public endpoint. */}
            <p className="mt-2 text-xs text-neutral-600">
              {f.pagePath ? (
                <>
                  Sent from <code className="break-all font-mono text-neutral-800">{f.pagePath}</code>
                </>
              ) : (
                "No page recorded (the landing page form sends none)"
              )}
            </p>
          </Card>
        </li>
      ))}
    </ul>
  );
}
